import { useState } from "react";
import { Alert, Button, Form, Input } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface LoginForm {
  username: string;
  password: string;
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const from = (loc.state as { from?: string } | null)?.from ?? "/";

  async function onFinish(values: LoginForm) {
    setErr(null);
    setBusy(true);
    try {
      await login(values.username, values.password);
      nav(from, { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(ellipse at 70% 20%, #2d3748 0%, #1a202c 55%, #0b0f17 100%)",
        padding: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Fire glow orbs */}
      <div
        style={{
          position: "absolute",
          top: -120,
          right: -120,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(246,176,0,0.22) 0%, rgba(246,176,0,0) 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -160,
          left: -140,
          width: 460,
          height: 460,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,61,6,0.18) 0%, rgba(255,61,6,0) 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Subtle grid */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(246,176,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(246,176,0,0.04) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* Card */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 400,
          background: "rgba(26,32,44,0.78)",
          backdropFilter: "blur(22px)",
          WebkitBackdropFilter: "blur(22px)",
          border: "1px solid rgba(246,176,0,0.18)",
          borderRadius: 18,
          padding: "40px 34px 32px",
          boxShadow:
            "0 28px 90px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 60px rgba(246,176,0,0.08)",
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              display: "inline-flex",
              width: 64,
              height: 64,
              background: "linear-gradient(135deg, #f6b000 0%, #ff3d06 100%)",
              borderRadius: 18,
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 18,
              boxShadow:
                "0 12px 36px rgba(246,176,0,0.45), 0 0 0 1px rgba(255,255,255,0.1) inset",
            }}
          >
            <svg viewBox="0 0 64 64" width="36" height="36" fill="none">
              <path
                d="M12 24 Q 22 16, 32 24 T 52 24"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                opacity="0.95"
              />
              <path
                d="M12 34 Q 22 26, 32 34 T 52 34"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                opacity="0.75"
              />
              <path
                d="M12 44 Q 22 36, 32 44 T 52 44"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                opacity="0.55"
              />
            </svg>
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "#f7f8fa",
              letterSpacing: 0.4,
              marginBottom: 4,
              fontFamily:
                'Poppins, "SVN-Poppins", Inter, -apple-system, sans-serif',
            }}
          >
            LeAnh Console
          </div>
          <div style={{ fontSize: 12.5, color: "#a0aec0", letterSpacing: 0.3 }}>
            sFlow Traffic Analytics · Sign in
          </div>
        </div>

        {/* Error */}
        {err && (
          <Alert
            type="error"
            message={err}
            showIcon
            closable
            style={{
              marginBottom: 16,
              background: "rgba(255,64,64,0.1)",
              border: "1px solid rgba(255,64,64,0.28)",
              borderRadius: 8,
              color: "#fca5a5",
            }}
            onClose={() => setErr(null)}
          />
        )}

        {/* Form */}
        <Form<LoginForm> layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            name="username"
            rules={[{ required: true, message: "Username required" }]}
            style={{ marginBottom: 12 }}
          >
            <Input
              prefix={<UserOutlined style={{ color: "#a0aec0" }} />}
              placeholder="Username"
              size="large"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: 10,
                color: "#f7f8fa",
                fontSize: 13,
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: "Password required" }]}
            style={{ marginBottom: 22 }}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#a0aec0" }} />}
              placeholder="Password"
              size="large"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: 10,
                color: "#f7f8fa",
                fontSize: 13,
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={busy}
              style={{
                height: 44,
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                background:
                  "linear-gradient(135deg, #f6b000 0%, #ff3d06 100%)",
                border: "none",
                boxShadow: "0 10px 26px rgba(246,176,0,0.42)",
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              Sign in
            </Button>
          </Form.Item>
        </Form>

        {/* Footer */}
        <div
          style={{
            textAlign: "center",
            marginTop: 22,
            fontSize: 11,
            color: "#4a5568",
            letterSpacing: 0.5,
          }}
        >
          LeAnh Console · sflow sensor platform
        </div>
      </div>
    </div>
  );
}
